import { useCallback, useMemo } from "react";
import ConfigUtils from "../../../utils/Config";
import IEvent from "../../../types/Event";
import ClaseUtils from "../../../utils/Clase";
import IPaciente from "../../../types/Paciente";
import AgendaUtils from "../../../utils/Agenda";
import ICita from "../../../types/Cita";
import IActividad from "../../../types/Actividad";
import { SlotInfo } from "react-big-calendar";
import AgendaService from "../../../services/AgendaService";
import { EstadoFacturacion } from "../../facturacion/common/EstadoFacturacionIcon";

export const useAgendaHandlers = ({
    pacientes,
    actividades,
    isEditCita,
    setEvent,
    setModalClassEvent,
    setModalOutOffice,
    setPacientesIds,
    setProfesorId,
    setSalaClassId,
    setStartClass,
    setDuracion,
    setComentariosClass,
    setModalEvent,
    setCita,
    setIsEditCita,
    setPacienteId,
    setEspecialistaId,
    setSalaId,
    setStart,
    setStartTime,
    setEndTime,
    setRepetirId,
    setEstadoFacturacionCita,
    setTerminarRepetir,
    setErrorPaciente,
    setModalCita,
    setModalClass
}: {
    pacientes: IPaciente[];
    actividades: IActividad[];
    isEditCita: boolean;
    setEvent: (event: IEvent | undefined) => void;
    setModalClassEvent: (open: boolean) => void;
    setModalOutOffice: (open: boolean) => void;
    setPacientesIds: (pacientesIds: IPaciente[]) => void;
    setProfesorId: (profesorId: number) => void;
    setSalaClassId: (salaId: number) => void;
    setStartClass: (date: Date) => void;
    setDuracion: (duracion: number) => void;
    setComentariosClass: (comentario: string) => void;
    setModalEvent: (open: boolean) => void;
    setCita: (cita: ICita | undefined) => void;
    setIsEditCita: (isEdit: boolean) => void;
    setPacienteId: (pacienteId: string) => void;
    setEspecialistaId: (especialistaId: number) => void;
    setSalaId: (salaId: number) => void;
    setStart: (date: Date) => void;
    setStartTime: (date: Date) => void;
    setEndTime: (date: Date) => void;
    setRepetirId: (repetirId: number) => void;
    setEstadoFacturacionCita: (estado: EstadoFacturacion) => void;
    setTerminarRepetir: (date: Date) => void;
    setErrorPaciente: (errorPaciente: string) => void;
    setModalCita: (open: boolean) => void;
    setModalClass: (open: boolean) => void;
}) => {
    const actividadesArray = useMemo(
        () => ConfigUtils.getActividadesArray(actividades),
        [actividades]
    );
    
    const selectCita = useCallback(
        async (event: IEvent) => {
            if (!event) return;
            if (event.allDay) return;
            if (Object.keys(event.resource).length === 0) {
                setEvent(event);
                setModalOutOffice(true);
                return;
            }

            if (actividadesArray.includes(event.title.toLowerCase())) {
                setEvent(event);
                const selectedClass = ClaseUtils.formatClassEvent(event);
                if (selectedClass === undefined || selectedClass.end === undefined) return;
                setPacientesIds(pacientes.filter((p) => selectedClass.pacientesIds.includes(p._id)));
                setProfesorId(selectedClass.especialistaId);
                setSalaClassId(selectedClass?.salaId);
                setStartClass(selectedClass.start);
                setDuracion(Math.abs(Math.round(((selectedClass.end.getTime() - selectedClass.start.getTime()) / 1000) / 60)));
                setComentariosClass(selectedClass.comentarios ? selectedClass.comentarios : '');
                setModalClassEvent(true);
            } else {
                setEvent(event);
                const selectedCita = AgendaUtils.formatEvent(event);
                setCita(selectedCita);
                if (selectedCita === undefined) return;
                setIsEditCita(true);
                setPacienteId(selectedCita.pacienteId);
                setEspecialistaId(selectedCita?.especialistaId);
                setSalaId(selectedCita?.salaId);
                setStart(selectedCita.start);
                setStartTime(selectedCita.start);
                setEndTime(selectedCita.end);
                setRepetirId(selectedCita?.repetirId);
                setEstadoFacturacionCita(EstadoFacturacion.LOADING);
                setModalEvent(true);
                const response = await AgendaService.getEstadoFacturacionCita(selectedCita._id!);
                setEstadoFacturacionCita(response.data.estadoFacturacion);
            }
        },
        [pacientes, actividades, setEvent, setModalClassEvent, setModalOutOffice, setPacientesIds, setProfesorId, setSalaClassId, setStartClass, setDuracion, setComentariosClass, setModalEvent, setCita, setIsEditCita, setPacienteId, setEspecialistaId, setSalaId, setStart, setStartTime, setEndTime, setRepetirId, setEstadoFacturacionCita]
    );

    const closeModalCita = useCallback( 
        () => {
            if (!isEditCita) {
                setStart(new Date());
                setStartTime(new Date());
                setEndTime(new Date());
                setEspecialistaId(0);
                setSalaId(0);
                setPacienteId('');
                setIsEditCita(false);
                setRepetirId(0);
                setTerminarRepetir(new Date(new Date().setHours(23, 59, 59, 999)));
            }
            setErrorPaciente('');
            setModalCita(false);
        },
        [setStart, setStartTime, setEndTime, setEspecialistaId, setSalaId, setPacienteId, setIsEditCita, setRepetirId, setTerminarRepetir, setErrorPaciente, setModalCita]
    );

    const closeModalClass = useCallback(
        () => {
            setStartClass(new Date());
            setProfesorId(0);
            setSalaClassId(0);
            setDuracion(60);
            setPacientesIds([]);
            setModalClass(false);
            setComentariosClass('');
        },
        [setStartClass, setProfesorId, setSalaClassId, setDuracion, setPacientesIds, setComentariosClass, setModalClass]
    );

    const closeModalEvent = useCallback(
        () => {
            setModalEvent(false);
            setStart(new Date());
            setStartTime(new Date());
            setEndTime(new Date());
            setEspecialistaId(0);
            setSalaId(0);
            setPacienteId('');
            setIsEditCita(false);
            setRepetirId(0);
        },
        [setModalEvent, setStart, setStartTime, setEndTime, setEspecialistaId, setSalaId, setPacienteId, setIsEditCita, setRepetirId]
    );

    const closeModalClassEvent = useCallback(
        () => {
            setModalClassEvent(false);
            setStartClass(new Date());
            setProfesorId(0);
            setSalaClassId(0);
            setDuracion(60);
            setPacientesIds([]);
            setComentariosClass('');
        },
        [setModalClassEvent, setStartClass, setProfesorId, setSalaClassId, setDuracion, setPacientesIds, setComentariosClass]
    );

    const abrirNuevaCita = useCallback(
        (inicio: Date, fin: Date) => {
            // Cita nueva: sin evento seleccionado (podía quedar el de una cita o una clase abierta antes)
            setEvent(undefined);
            setStart(new Date(inicio));
            setStartTime(new Date(inicio));
            setEndTime(new Date(fin));
            setModalCita(true);
        },
        [setEvent, setStart, setStartTime, setEndTime, setModalCita]
    );

    const createCita = useCallback(
        (slotInfo: SlotInfo) => abrirNuevaCita(slotInfo.start, slotInfo.end),
        [abrirNuevaCita]
    );

    // Botón «Nueva cita» (en táctil seleccionar un hueco exige mantener pulsado): hoy, a la siguiente media hora, con una
    // hora de duración (como al cambiar la hora de inicio)
    const nuevaCita = useCallback(
        () => {
            const inicio = new Date();
            inicio.setMinutes(inicio.getMinutes() < 30 ? 30 : 60, 0, 0);
            const fin = new Date(inicio);
            fin.setMinutes(fin.getMinutes() + 60);
            abrirNuevaCita(inicio, fin);
        },
        [abrirNuevaCita]
    );

    return {
        selectCita,
        closeModalCita,
        closeModalClass,
        closeModalEvent,
        closeModalClassEvent,
        createCita,
        nuevaCita,
    };
};